import { AccountBalanceWalletRounded } from "@mui/icons-material";
import { Chip } from "@mui/material";
import { useEffect, useState } from "react";

import { getSocket } from "@/lib/socket";
import { getMyBalance } from "@/services";
import { useSelector } from "@/store";
import { formatBalance, handleError } from "@/utils";

// Server-side socket payload emitted from `emitWalletBalance` in
// modules/payment/libs/notify.ts.
interface WalletUpdate {
  userId: string;
  currency: string;
  balance: number;
  locked: number;
  bonus: number;
}

const HeaderBalanceChip = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string>("");

  const token = useSelector((state) => state.auth.token);
  const userId = useSelector((state) => state.auth.user?._id);
  const userCurrency = useSelector((state) => state.auth.user?.currency);

  // Fetch on mount and refetch whenever the visitor's selected currency changes
  // (e.g. they change their own currency), so the header balance reflects the
  // new currency immediately. getMyBalance reads the currency fresh server-side.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getMyBalance();
        if (cancelled) return;
        setBalance(res.balance);
        setCurrency(res.currency);
      } catch (error) {
        handleError(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userCurrency]);

  // Live updates via socket. Backend's adminDeposit/adminWithdraw call
  // `emitWalletBalance` for both the receiving user and the performing user;
  // either way the visitor lands in `io.to(userId)` and we get a payload.
  useEffect(() => {
    if (!token || !userId) return;
    const socket = getSocket(token);
    if (!socket) return;

    const handler = (payload: WalletUpdate) => {
      // Only react when the payload is for the visitor's selected currency
      // (or we have no currency yet, in which case adopt whatever arrives).
      if (currency && payload.currency !== currency) return;
      setBalance(payload.balance);
      setCurrency(payload.currency);
    };
    socket.on("wallet:update", handler);

    return () => {
      socket.off("wallet:update", handler);
    };
  }, [token, userId, currency]);

  return (
    <Chip
      icon={<AccountBalanceWalletRounded fontSize="small" />}
      label={balance === null ? "-" : formatBalance(balance, currency || "USD")}
      color="primary"
      variant="outlined"
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );
};

export default HeaderBalanceChip;
